/**
 * An attached fixture as the Add Outcomes modal groups by — `.m-row` with
 * `.m-row-chev-left` in the prototype's modals.css (CAE-1553).
 *
 * The kit's `FixtureCard`, with no selection: nothing here selects a fixture,
 * so the chevron leads the row in the space the checkbox would have taken, and
 * `.m-row.is-haspicks` lights the border when outcomes *under* the row are
 * picked. Its siblings are `attached-fixture-row.tsx` and
 * `catalogue-fixture-row.tsx`, the same card over the other two payloads.
 */
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { Theme } from '@mui/material/styles';
import { resolveFixtureLabel } from '@s2w/core';
import type { CatalogueLocale, CatalogueMarket, CatalogueOutcome } from '../../../../api/catalogue';
import type { ContestDetailFixture } from '../../../../api/contests';
import {
  AdminEmptySection,
  FixtureCard,
  MarketBadge,
  NoFixturesIcon,
  RefreshIcon,
  StatusPill,
} from '../../../../components/ui';
import { crestTeam, toFixturePillState } from '../../overview/fixture-display';
import { formatFixtureStatusLabel } from '../../detail-tabs/contest-detail-tab-utils';
import { FixtureMetaClauses } from '../../fixture-meta-clauses';
import { MarketPickCard } from './market-pick-card';

/**
 * How tall the market list may grow before it scrolls inside itself — the cap
 * `fixture-market-drilldown.tsx` records, for the same reason: a real fixture
 * carries 141 markets, and expanding one would push every other attached
 * fixture off the screen.
 */
const MARKET_LIST_MAX_HEIGHT = 420;

export interface FixturePickRowProps {
  fixture: ContestDetailFixture;
  markets: readonly CatalogueMarket[];
  locale: CatalogueLocale;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  openMarkets: ReadonlySet<string>;
  onMarketOpenChange: (marketExternalId: string, open: boolean) => void;
  pendingBulk: ReadonlySet<string>;
  selectedIds: ReadonlySet<string>;
  onReelIds: ReadonlySet<string>;
  hideOnReel: boolean;
  /** This fixture's markets are still in flight — not "the feed sent none". */
  marketsPending: boolean;
  /** This fixture's markets request failed — also not "the feed sent none". */
  marketsFailed: boolean;
  onRetryMarkets: () => void;
  onOutcomeToggle: (market: CatalogueMarket, outcome: CatalogueOutcome, selected: boolean) => void;
  onBulkSelect: (
    market: CatalogueMarket,
    outcomes: CatalogueOutcome[],
    selected: boolean,
    loaded: boolean,
  ) => void;
  /** How many outcomes under this fixture are picked — `.m-pickbadge`. */
  pickedHere: number;
}

/**
 * `.m-pickbadge` — the running count of picks made under this fixture.
 *
 * Solid accent with white text, as `modals.css:240` draws it — not the soft
 * ground with accent text the other chips on this row use. It is the one badge
 * here that reports something the operator just did, and it sits beside a
 * status pill and a market count that merely describe the fixture; the fill is
 * what separates "you picked 4" from "this is scheduled".
 */
const PickBadge = ({ count }: { count: number }) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '5px 10px',
      borderRadius: `${theme.tokens.radius.control}px`,
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '-0.005em',
      backgroundColor: theme.tokens.color.accent,
      color: theme.tokens.color.surface,
      whiteSpace: 'nowrap',
    })}
  >
    <Box
      component="span"
      sx={(theme: Theme) => ({ fontFamily: theme.tokens.font.mono, fontWeight: 600 })}
    >
      {count}
    </Box>
    <Box component="span" sx={{ fontWeight: 500, opacity: 0.9 }}>
      picked
    </Box>
  </Box>
);

export const FixturePickRow = ({
  fixture,
  markets,
  locale,
  expanded,
  onExpandedChange,
  openMarkets,
  onMarketOpenChange,
  pendingBulk,
  selectedIds,
  onReelIds,
  hideOnReel,
  marketsPending,
  marketsFailed,
  onRetryMarkets,
  onOutcomeToggle,
  onBulkSelect,
  pickedHere,
}: FixturePickRowProps) => {
  const label = resolveFixtureLabel(fixture);

  return (
    <FixtureCard
      testId={`outcome-fixture-${fixture.externalId}`}
      home={crestTeam(fixture.homeTeam || label)}
      away={fixture.awayTeam ? crestTeam(fixture.awayTeam) : undefined}
      title={label}
      chevron="leading"
      highlighted={pickedHere > 0}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      expandLabel={label}
      meta={<FixtureMetaClauses fixture={fixture} kickoffTracking="-0.01em" />}
      side={
        <>
          <StatusPill
            state={toFixturePillState(fixture.status)}
            size="sm"
            label={formatFixtureStatusLabel(fixture.status)}
          />
          {pickedHere > 0 ? <PickBadge count={pickedHere} /> : null}
          {/*
           * The contest payload's own counts, as the two sibling rows pass them.
           * `markets.length` is the *filtered* list and is empty while the fetch
           * is in flight, so the badge would read "No markets" on a fixture the
           * payload already knows carries 141.
           */}
          <MarketBadge
            marketCount={fixture.marketCount}
            outcomeCount={fixture.outcomeCount}
            queued={marketsPending}
          />
        </>
      }
    >
      <Box
        sx={(theme: Theme) => ({
          borderTop: theme.tokens.border.default,
          backgroundColor: theme.tokens.color.surfaceMuted,
          padding: '12px 14px',
        })}
      >
        {marketsPending ? (
          <Box sx={{ display: 'grid', placeItems: 'center', padding: '24px' }}>
            <CircularProgress size={20} aria-label={`Loading markets for ${label}`} />
          </Box>
        ) : marketsFailed ? (
          /*
           * A failed request is not an unsynced fixture. Drawn as one it sends
           * the operator to Edit › Fixtures to run a sync that cannot fix a 500
           * or a dropped connection — the same mistake the in-flight branch
           * above exists to avoid, and the one `add-fixtures-modal.tsx` names
           * outright ("A failed request is not an empty catalogue").
           */
          <AdminEmptySection
            tone="warn"
            icon={<RefreshIcon />}
            title="Couldn’t load this fixture’s markets"
            subtitle="The market list didn’t come back. Retry, or close and reopen the picker."
            action={{ label: 'Retry', icon: <RefreshIcon />, onClick: onRetryMarkets }}
          />
        ) : markets.length === 0 ? (
          <AdminEmptySection
            icon={<NoFixturesIcon />}
            title="No markets synced for this fixture"
            subtitle="Sync this fixture's markets from Edit › Fixtures, then come back to pick outcomes from them."
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: `${MARKET_LIST_MAX_HEIGHT}px`,
              overflowY: 'auto',
            }}
          >
            {markets.map((market) => (
              <MarketPickCard
                key={market.externalId}
                market={market}
                locale={locale}
                open={openMarkets.has(market.externalId)}
                onOpenChange={(next) => onMarketOpenChange(market.externalId, next)}
                selectedIds={selectedIds}
                onReelIds={onReelIds}
                hideOnReel={hideOnReel}
                onOutcomeToggle={onOutcomeToggle}
                onBulkSelect={onBulkSelect}
                pendingBulkSelect={pendingBulk.has(market.externalId)}
              />
            ))}
          </Box>
        )}
      </Box>
    </FixtureCard>
  );
};
